import React, { FC, useRef } from 'react'

import { DefaultFeatureFlags, usePRFile } from '@sketch-hq/sketch-web-renderer'
import { ErrorMessage } from '@sketch/components'
import { IS_EMBEDDED } from '@sketch/constants'
import { WebRendererCanvas } from 'components/WebRendererCanvas'
import { useCanvasPresentationFile } from 'components/WebRendererCanvas/hooks'
import { useTheme } from 'styled-components'

import { ArtboardAnnotationOverlay } from 'modules/annotations/components'
import { DraftCommentProvider } from 'modules/annotations/components/DraftCommentContext'
import LoadingState from 'modules/shares/components/LoadingState'
import { PageCanvasError } from 'modules/shares/PageCanvasView/components/PageCanvasError'

import { ArtboardDetailInspectorOverlay } from '../ArtboardDetailInspectorOverlay'
import { ArtboardOriginContent } from '../ArtboardOriginContent'
import { useCanvasAnnotations } from 'modules/annotations/hooks'
import { useInspectorContext, useInspectorHoveredElement } from '../../hooks'

import * as S from './ArtboardDetailCanvas.styles'
import {
  ArtboardDetailInspectorContextMenu,
  useInspectorContextMenu,
} from '../ArtboardDetailInspectorContextMenu'
import { ArtboardDetailInspectorTestOverlay } from '../ArtboardDetailInspectorTestOverlay'
import { DocumentProcessing } from 'modules/collections/components/CollectionItemEmptyState/CollectionItemEmptyState.styles'
import {
  isArtboardElement,
  SketchElement,
} from '../../hooks/inspector/sketchSceneLib'

interface ArtboardDetailCanvasProps {
  pageUUID: string
  artboardUUID: string
}

const ARTBOARD_DETAIL_CANVAS_FEATURE_FLAGS: Partial<
  typeof DefaultFeatureFlags
> = {
  canvasLayersEvents: true,
}

const IS_RUNNING_TESTS =
  process.env.JEST_WORKER_ID !== undefined || window.Cypress

export const ArtboardDetailCanvas: FC<ArtboardDetailCanvasProps> = ({
  pageUUID,
  artboardUUID,
}) => {
  const canvasContainerRef = useRef<HTMLDivElement>(null)

  const theme = useTheme()
  const {
    isInspectorActive,
    clickHandler,
    mouseMoveHandler,
  } = useInspectorContext()

  const {
    share,
    page,
    versionShortId,
    hasError,
    isLoading,
    isRendering,
  } = useCanvasPresentationFile({
    pageUUID,
  })

  const prFile = usePRFile()
  const bounds = prFile?.getRootNode()?.getAbsoluteExtent() ?? {
    width: 0,
    height: 0,
    x: 0,
    y: 0,
  }

  const {
    isPlacingDraftAnnotation,
    onMouseDown,
    onMouseUp,
    onTouchEnd,
    onTouchStart,
  } = useCanvasAnnotations({
    shareIdentifier: share.identifier,
    permanentPageId: pageUUID,
    permanentArtboardId: artboardUUID,
  })

  const {
    handleContextMenuEvent,
    contextMenuPosition,
    handleCloseContextMenu,
  } = useInspectorContextMenu(canvasContainerRef)
  const hoveredElement = useInspectorHoveredElement()

  if (isLoading) {
    return <LoadingState />
  }

  if (isRendering) {
    return (
      <ErrorMessage
        title="Rendering Canvas…"
        description="The canvas is rendering. When it’s ready, it will appear here."
        icon={<DocumentProcessing />}
      />
    )
  }

  if (hasError || !page) {
    return (
      <PageCanvasError
        description={
          <>
            There was a problem fetching the data. You can find more information
            on our <a href="https://status.sketch.com/">Status Page</a>.
          </>
        }
        showReloadCanvasAction
      />
    )
  }

  return (
    <S.Wrapper ref={canvasContainerRef}>
      <ArtboardDetailInspectorContextMenu
        contextMenuPosition={contextMenuPosition}
        onCloseMenu={handleCloseContextMenu}
      />
      <WebRendererCanvas
        pageUUID={pageUUID}
        artboardUUID={artboardUUID}
        page={page}
        versionShortId={versionShortId}
        shareIdentifier={share.identifier}
        backgroundColor={theme.colors.background.secondary.B}
        featureFlags={ARTBOARD_DETAIL_CANVAS_FEATURE_FLAGS}
        webRenderCursor={getWebRenderCursor(
          isPlacingDraftAnnotation,
          hoveredElement
        )}
        webRenderContainerProps={{
          onContextMenu: handleContextMenuEvent,
          onClick: clickHandler,
          onMouseMove: mouseMoveHandler,
          onMouseDown,
          onMouseUp,
          onTouchEnd,
          onTouchStart,
        }}
      >
        {!IS_EMBEDDED && (
          <ArtboardOriginContent>
            <DraftCommentProvider>
              <ArtboardAnnotationOverlay
                shareIdentifier={share.identifier}
                permanentArtboardId={artboardUUID}
                artboardWidth={bounds.width}
                artboardHeight={bounds.height}
              />
            </DraftCommentProvider>
          </ArtboardOriginContent>
        )}
        {isInspectorActive && prFile && (
          <>
            {IS_RUNNING_TESTS && <ArtboardDetailInspectorTestOverlay />}
            <ArtboardDetailInspectorOverlay
              canvasContainerRef={canvasContainerRef}
            />
          </>
        )}
      </WebRendererCanvas>
    </S.Wrapper>
  )
}

function getWebRenderCursor(
  isPlacingDraftAnnotation: boolean | undefined,
  hoveredElement: SketchElement | null
) {
  if (isPlacingDraftAnnotation) {
    return 'copy'
  }

  if (hoveredElement && !isArtboardElement(hoveredElement)) {
    return 'auto'
  }

  return undefined
}
